import {
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0TagStatus, F0Text, StatusVariant } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  FormAnswerInline,
  FormResponsesInline,
  useAnswerForm,
  useDeleteAnswers,
} from 'modules/forms'
import { useFormMemberships } from 'modules/trainings/components/content/FormMembershipContext'
import { titleStyle } from 'modules/trainings/components/content/styles'
import 'modules/trainings/components/content/index.styles.css'
import {
  extractFormFromQuizBlock,
  populateFormWithAnswers,
} from 'modules/trainings/components/content/items/quiz/FormQuizHelper'
import { usePreviewMode } from 'modules/trainings/components/content/preview/PreviewModeProvider'
import {
  handleSubmitPreviewMode as handleSubmitPreview,
  patchElementsForPreview,
} from 'modules/trainings/components/content/preview/quizPreviewUtils'
import {
  ContentConsumerCallbacks,
  ContentConsumerProps,
} from 'modules/trainings/components/content/types'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'

const TOAST_TIME = 2000

export const QuizBlockConsumer = forwardRef(function QuizBlockConsumer(
  { contentItem, updateNextButton }: ContentConsumerProps<QuizBlock>,
  ref: Ref<ContentConsumerCallbacks>
) {
  const { isPreviewMode } = usePreviewMode()
  const containerRef = useRef<HTMLDivElement>(null)
  const [baseForm, setBaseForm] = useState(() => extractFormFromQuizBlock(contentItem))
  const [submissionResult, setSubmissionResult] = useState<'passed' | 'failed' | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)
  const [numberOfAnswers, setNumberOfAnswers] = useState(0)
  const [, forceUpdate] = useReducer((x) => x + 1, 0)

  const { saveAnswers, savingAnswers } = useAnswerForm()
  const { deleteAnswers } = useDeleteAnswers()
  const { getMembershipByFormId } = useFormMemberships()

  useEffect(() => {
    setBaseForm(extractFormFromQuizBlock(contentItem))
  }, [contentItem])

  const membership = useMemo(
    () => (baseForm?.id && !isPreviewMode ? getMembershipByFormId(baseForm.id) : undefined),
    [baseForm?.id, getMembershipByFormId, isPreviewMode]
  )

  const membershipId = membership?.id
  const answers = useMemo(() => membership?.answers || [], [membership])
  const score = Number(membership?.score)
  const hasAnswersFromQuery = answers.length > 0

  const hasAnswers = isRetrying
    ? submissionResult !== null
    : hasAnswersFromQuery ||
      submissionResult !== null ||
      (isPreviewMode && contentItem.status === 'completed')
  const hasToRetry =
    !isRetrying && ((hasAnswersFromQuery && score !== 100) || submissionResult === 'failed')
  const isCompleted = (hasAnswersFromQuery && score === 100) || submissionResult === 'passed'

  const form = useMemo(() => {
    if (baseForm && hasAnswersFromQuery && answers.length > 0) {
      populateFormWithAnswers(baseForm, answers)
    }
    return baseForm
  }, [baseForm, hasAnswersFromQuery, answers])

  useEffect(() => {
    if (form && (hasAnswersFromQuery || (isPreviewMode && contentItem.status === 'completed'))) {
      setNumberOfAnswers(form.numberOfAnswers())
    }
  }, [form, hasAnswersFromQuery, isPreviewMode, contentItem.status])

  useEffect(() => {
    updateNextButton()
  }, [submissionResult, numberOfAnswers, isRetrying, updateNextButton])

  useEffect(() => {
    if (isRetrying && form && !hasAnswersFromQuery) {
      form.resetFormAnswers()
    }
  }, [isRetrying, form, hasAnswersFromQuery])

  useEffect(() => {
    if (isPreviewMode && submissionResult === 'passed') {
      contentItem.status = 'completed'
    }
  }, [isPreviewMode, submissionResult, contentItem])

  useEffect(() => {
    if (isPreviewMode && form && contentItem.status === 'completed') {
      setSubmissionResult('passed')

      // Mark all questions as correct in preview mode when completed
      form.elements.forEach((element) => {
        if (element.hasCorrectAnswer) {
          if (!element.answer) {
            element.answer = { answer: '', options: [], isCorrect: true }
          } else {
            element.answer.isCorrect = true
          }
        }
      })

      // When revisiting a completed quiz, show correct answers
      patchElementsForPreview(form.elements, true)
      setNumberOfAnswers(form.numberOfAnswers())
      forceUpdate()
    }
  }, [isPreviewMode, form, contentItem.status])

  const handleRetry = useCallback(async () => {
    if (!membershipId) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
        time: TOAST_TIME,
      })
      return
    }

    await deleteAnswers({
      membershipId,
      onSuccess: () => {
        form?.resetFormAnswers()
        setNumberOfAnswers(0)
        setSubmissionResult(null)
        setIsRetrying(true)
        updateNextButton()
      },
      onError: () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
          time: TOAST_TIME,
        })
      },
    })
  }, [membershipId, deleteAnswers, form, updateNextButton])

  const scrollToTop = useCallback(() => {
    const element = containerRef.current
    if (!element) return

    let scrollableParent = element.parentElement
    while (scrollableParent) {
      const overflowY = window.getComputedStyle(scrollableParent).overflowY
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollableParent.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      scrollableParent = scrollableParent.parentElement
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleSubmit = useCallback<() => Promise<boolean>>(async () => {
    if (isPreviewMode) {
      return handleSubmitPreview({
        form,
        hasToRetry,
        submissionResult,
        isCompleted,
        setNumberOfAnswers,
        setSubmissionResult,
        setIsRetrying,
        updateNextButton,
        scrollToTop,
      })
    }

    if (hasToRetry) {
      await handleRetry()
      scrollToTop()
      return false
    }

    if (submissionResult !== null || isCompleted) {
      return true
    }

    if (!form || !membershipId) {
      app.setFlash({ type: 'wrong', message: i18n.t('common.error'), time: TOAST_TIME })
      return false
    }

    await saveAnswers({
      form,
      membershipId,
      onSuccess: (answersFromMutation) => {
        if (answersFromMutation && answersFromMutation.length > 0) {
          populateFormWithAnswers(form, answersFromMutation)
          const allCorrect = answersFromMutation.every((answer) => answer.isCorrect === true)
          setSubmissionResult(allCorrect ? 'passed' : 'failed')
          setIsRetrying(false)
        }
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.content.consumer.quiz.submitted'),
          time: TOAST_TIME,
        })
        updateNextButton()
        scrollToTop()
      },
    })

    return false
  }, [
    isPreviewMode,
    form,
    hasToRetry,
    submissionResult,
    isCompleted,
    handleRetry,
    membershipId,
    saveAnswers,
    scrollToTop,
    updateNextButton,
  ])

  useImperativeHandle(ref, () => {
    return {
      handleNext: handleSubmit,
      nextEnabled: () => {
        if (isPreviewMode && contentItem.status === 'completed') return true
        if (hasToRetry) return true
        if (submissionResult !== null || isCompleted) return true
        return !!form && !form.hasUnansweredMandatoryElements()
      },
      nextLabel: () => {
        if (isPreviewMode && contentItem.status === 'completed') return ''
        if (isRetrying) {
          return i18n.t('common.submit')
        }
        if (hasToRetry) {
          return i18n.t('trainings.content.consumer.quiz.retry')
        }
        if (submissionResult !== null || isCompleted) return ''
        return i18n.t('common.submit')
      },
      nextLoading: () => {
        return false
      },
    }
  }, [
    handleSubmit,
    hasToRetry,
    form,
    isCompleted,
    submissionResult,
    isRetrying,
    isPreviewMode,
    contentItem,
  ])

  const { statusLabel, statusVariant } = useMemo((): {
    statusLabel: string
    statusVariant: StatusVariant
  } => {
    if (hasToRetry) {
      return {
        statusLabel: i18n.t('trainings.content.consumer.quiz.status.failed'),
        statusVariant: 'critical',
      }
    }
    if (isCompleted) {
      return {
        statusLabel: i18n.t('trainings.content.consumer.quiz.status.passed'),
        statusVariant: 'positive',
      }
    }
    if (numberOfAnswers > 0) {
      return {
        statusLabel: i18n.t('trainings.content.consumer.quiz.status.in_progress'),
        statusVariant: 'info',
      }
    }
    return {
      statusLabel: i18n.t('trainings.content.consumer.quiz.status.not_started'),
      statusVariant: 'neutral',
    }
  }, [hasToRetry, isCompleted, numberOfAnswers])

  if (!form) return null

  if (savingAnswers) {
    return (
      <Box
        position="absolute"
        gap="s8"
        alignItems="center"
        justifyContent="center"
        style={{ inset: '0' }}
      >
        <Spinner size="large" className="text-f1-icon-info" />
        <Text.Normal weight="bold">
          {i18n.t('trainings.content.consumer.quiz.saving.title')}
        </Text.Normal>
        <Text.Normal size="200">
          {i18n.t('trainings.content.consumer.quiz.saving.subtitle')}
        </Text.Normal>
      </Box>
    )
  }

  return (
    <Box ref={containerRef} width="full" alignItems="center" gap="s24">
      <Box width="full" gap="s24" className={'content_contentContainer'}>
        {isPreviewMode && (
          <F0Alert
            variant="info"
            title={i18n.t('trainings.content.consumer.preview.title')}
            description={i18n.t('trainings.content.consumer.preview.description')}
          />
        )}

        {hasToRetry && (
          <F0Alert
            variant="critical"
            title={i18n.t('trainings.content.consumer.quiz.did_not_pass')}
            description=""
          />
        )}

        {isCompleted && (
          <F0Alert
            variant="positive"
            title={i18n.t('trainings.content.consumer.quiz.passed')}
            description=""
          />
        )}

        <Box gap="s12">
          <F0Text content={contentItem.title} variant="body" style={titleStyle} />
          {form?.description && <F0Text content={form.description} variant="description" />}
          <Box flexDirection="row" gap="s12" alignItems="center">
            <F0TagStatus text={statusLabel} variant={statusVariant} />

            <F0Text content="|" variant="small" color="grey300" />

            <Box flexDirection="row" gap="s4" alignItems="center">
              <F0Text
                content={i18n.t('trainings.content.consumer.quiz.questions.label')}
                variant="description"
              />
              <F0Text
                content={`${form?.elements.length || 0}`}
                variant="body"
                style={{ fontWeight: 600 }}
              />
            </Box>
          </Box>
        </Box>

        {hasAnswers ? (
          <Box className={'content_normalFontWeight'} paddingBottom="s80">
            <FormResponsesInline form={form} titleSize="small" />
          </Box>
        ) : (
          <Box className={'content_normalFontWeight'} paddingBottom="s80">
            <FormAnswerInline
              form={form}
              onFormUpdated={() => {
                setNumberOfAnswers(form.numberOfAnswers())
                updateNextButton()
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
})
